import { useLocation, useParams } from 'react-router-dom';
import { useAppState } from 'customHooks/useAppState';

interface CampaignItemRoutes {
    index: () => string;
    show: (id: number) => string;
    create: () => string;
    listSponsors: () => string;
    listSponsorItems: (id: number) => string;
    createSponsored: (sponsorId?: number, id?: number) => string;
    edit: (id: number) => string;
    duplicate: (id: number) => string;
    exportQRCode: (id?: number) => string;
    exportQRCodeDetailed: (id?: number) => string;
    extend: (id: number) => string;
    declareWinner: (id: number) => string;
}

const useCampaignItemRoutes = (): CampaignItemRoutes => {
    const { apiUrl } = useAppState();
    const { pathname } = useLocation();
    const { account_id: accountId, campaign_id: campaignId } = useParams<{
        account_id: string;
        campaign_id: string;
    }>();

    const baseQRCodeExportUrl = `${apiUrl}/dashboard/s/files/qr_campaign_item?campaign_id=${campaignId}`;

    const index = () => `/accounts/${accountId}/campaigns/${campaignId}/auction/items`;

    const show = (itemId: number) =>
        `/accounts/${accountId}/campaigns/${campaignId}/auction/items/${itemId}`;

    const create = () => `/accounts/${accountId}/campaigns/${campaignId}/auction/items/new`;

    const edit = (itemId: number) =>
        `/accounts/${accountId}/campaigns/${campaignId}/auction/items/${itemId}/edit`;

    const duplicate = (itemId: number) =>
        `/accounts/${accountId}/campaigns/${campaignId}/auction/items/${itemId}/duplicate`;

    const listSponsors = () =>
        `/accounts/${accountId}/campaigns/${campaignId}/auction/items/sponsors/`;

    const listSponsorItems = (sponsorId: number) =>
        `/accounts/${accountId}/campaigns/${campaignId}/auction/items/sponsors/${sponsorId}/items`;

    const createSponsored = (sponsorId?: number, templateId?: number) =>
        `/accounts/${accountId}/campaigns/${campaignId}/auction/items/sponsor/${sponsorId}/template/${templateId}`;

    const exportQRCodeDetailed = (itemId?: number) => {
        if (itemId) return `${baseQRCodeExportUrl}&campaign_item_id=${itemId}&version=3`;

        return `${baseQRCodeExportUrl}&version=3`;
    };

    const exportQRCode = (itemId?: number) => {
        if (itemId) return `${baseQRCodeExportUrl}&campaign_item_id=${itemId}`;

        return baseQRCodeExportUrl;
    };

    const extend = (itemId: number) =>
        `/accounts/${accountId}/campaigns/${campaignId}/auction/items/${itemId}/edit?extend=true`;

    const declareWinner = (itemId: number) => {
        const basePath = `/accounts/${accountId}/campaigns/${campaignId}/auction/items`;
        const expectedPath = `${basePath}/${itemId}`;
        const isItemDetailsView = pathname === expectedPath;

        return isItemDetailsView
            ? `${basePath}/${itemId}/declare-winner`
            : `${basePath}/declare-winner/${itemId}`;
    };

    return {
        index,
        show,
        create,
        listSponsors,
        listSponsorItems,
        createSponsored,
        edit,
        duplicate,
        exportQRCode,
        exportQRCodeDetailed,
        extend,
        declareWinner,
    };
};

export { useCampaignItemRoutes };
export type { CampaignItemRoutes };
